import React, { Component } from 'react'
import { AiOutlineSearch, AiFillTags } from "react-icons/ai"
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }

const LEVEL_STYLES = {
    1: { bg: 'rgba(37,99,235,0.08)', color: '#1e40af' },
    2: { bg: 'rgba(139,92,246,0.08)', color: '#5b21b6' },
    3: { bg: 'rgba(16,185,129,0.08)', color: '#065F46' },
    4: { bg: 'rgba(245,158,11,0.08)', color: '#92400E' },
}

// Sales Person filter (2026-09-01) — salesPersonNo comes straight from a
// MAS90 mirror table (see customerPriceLevel.ts's getList), which can hand
// it back as a number instead of a string, or padded with whitespace (fixed-
// width CHAR columns). The <select>'s value is always a plain string (the
// DOM coerces option values to text), so comparing it against a customer's
// raw salesPersonNo with strict `===` silently never matches when the types
// differ — the filter would look like it does nothing at all. Both sides of
// the comparison go through this first.
const normalizeSalesPerson = (v) => (v === undefined || v === null) ? '' : String(v).trim()

/**
 * Customer Price Levels — new admin page (2026-08-19, tiered pricing project).
 *
 * Client: "hay clientes con precios especiales, clientes nivel 1, 2, 3."
 * (extended 2026-08-26 to Level 4). This page manages ONLY the assignment
 * (which tier a customer is in) — the actual price per tier is set in Price
 * Management (product_pricing.level1/2/3/4Price).
 * A customer with no row here defaults to Level 1, same default the pricing
 * endpoints use (see customerPriceLevel.ts / productPricing.ts getMap).
 *
 * Only TypeUser === '1' can change a level; everyone else sees it read-only —
 * same gate as Price Management. Every change updates customer_price_level
 * directly (no history table for this one — the level itself, not a price, so
 * lower audit stakes; product_pricing_history already covers the $ side).
 *
 * Route grouping (2026-08-20, client's ask — "agrupar por ruta, como en Price
 * Management"): route comes from MAS90's UDF_RUTA (AR_Customer), not from
 * anything in the local `customer` table — see customerPriceLevel.ts's
 * getList header for the mirror-table join. Same grouped-pagination shape as
 * ProductPricing.js: `routes` is the current page's groups (route name +
 * customer count), `customers` is every customer across those routes, never
 * split mid-route across pages. `pageSize` counts ROUTES, not customers.
 */
export default class CustomerPriceLevels extends Component {
    state = {
        idCompany: getValueCookie('CompanyId') || '',
        customers: [],
        // Route groups on the CURRENT page, in display order, each with
        // {route, customerCount} — route === '' means no UDF_RUTA assigned,
        // displayed as "(No route)". `customers` holds every customer across
        // ALL these routes (never truncated mid-route).
        routes: [],
        // Route filter's dropdown options — ALL routes for this company,
        // independent of search/pagination (fetched once on mount).
        routeOptions: [],
        routeFilter: undefined, // undefined = all routes; '' = "(No route)" specifically
        // Sales Person filter (2026-09-01, client's ask) — unlike routeOptions,
        // there's no dedicated "list all sales persons" endpoint for this one:
        // the client asked for it to come straight from customer-level/list's
        // own `salesPersonNo` field, so the dropdown's options are the
        // distinct values seen across fetchCustomers() responses so far,
        // accumulated in fetchCustomers() (see its own comment). Same
        // undefined-means-all convention as routeFilter.
        salesPersonOptions: [],
        salesPersonFilter: undefined, // undefined = all sales persons
        search: '',
        page: 1,
        pageSize: 10,
        total: 0,
        loading: false,
        loaded: false,
        errorMessage: '',
        savingCustomerNo: '',
    }

    isAdmin = getValueCookie('TypeUser') === '1'

    componentDidMount() {
        this.fetchCustomers()
        this.fetchRouteOptions()
    }

    async fetchCustomers() {
        this.setState({ loading: true, errorMessage: '' })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/customer-level/list', {
                idCompany: Number(this.state.idCompany),
                search: this.state.search || undefined,
                route: this.state.routeFilter,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false, loaded: true, errorMessage: 'Could not load customers.' })
                return
            }
            const customers = (resp.data && Array.isArray(resp.data.customers)) ? resp.data.customers : []
            const routes = (resp.data && Array.isArray(resp.data.routes)) ? resp.data.routes : []
            // Sales Person options — no dedicated endpoint for this filter,
            // and the backend's getList() doesn't accept/apply a
            // salesPersonNo filter either (confirmed 2026-09-01 — it already
            // SELECTs c.salesPersonNo per customer, but never adds it to its
            // WHERE, so passing it as a request param is a silent no-op).
            // Per client's ask, this filter is handled ENTIRELY client-side
            // from data this same endpoint already returns — see render(),
            // where salesPersonFilter narrows `customers` per route without
            // any extra request. Options here are the distinct salesPersonNo
            // values seen on customers returned so far, merged with whatever
            // was already collected (keeps options from earlier
            // searches/pages/route-filters instead of shrinking every fetch).
            const salesPersonOptions = Array.from(new Set([
                ...this.state.salesPersonOptions,
                ...customers.map((c) => normalizeSalesPerson(c.salesPersonNo)).filter((sp) => sp !== ''),
            ])).sort()
            this.setState({ loading: false, loaded: true, customers, routes, salesPersonOptions, total: resp.total || routes.length })
        } catch (error) {
            this.setState({ loading: false, loaded: true, errorMessage: error.message || 'Error loading customers.' })
        }
    }

    /** Populates the route filter's options — independent of search/page. */
    async fetchRouteOptions() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/customer-level/routes', { idCompany: Number(this.state.idCompany) })
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ routeOptions: resp.data })
            }
        } catch (error) {
            console.error('fetchRouteOptions failed:', error)
        }
    }

    async setLevel(customerNo, priceLevel) {
        this.setState({ savingCustomerNo: customerNo })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/customer-level/upsert', {
                customerNo,
                idCompany: Number(this.state.idCompany),
                priceLevel,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ savingCustomerNo: '', errorMessage: 'Could not update ' + customerNo + '.' })
                return
            }
            this.setState((prev) => ({
                savingCustomerNo: '',
                customers: prev.customers.map((c) => c.customerNo === customerNo ? { ...c, priceLevel } : c),
            }))
        } catch (error) {
            this.setState({ savingCustomerNo: '', errorMessage: error.message || 'Error updating level.' })
        }
    }

    render() {
        const { customers, routes, routeOptions, routeFilter, salesPersonOptions, salesPersonFilter, search, page, pageSize, total, loading, errorMessage, savingCustomerNo } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        const customersByRoute = {}
        for (const c of customers) {
            const key = c.route || ''
            if (!customersByRoute[key]) customersByRoute[key] = []
            customersByRoute[key].push(c)
        }
        // Sales Person filter, applied entirely client-side (2026-09-01) on
        // top of the route grouping above — customersByRoute itself is
        // untouched, so when salesPersonFilter is undefined ("All") every
        // route below renders with the exact same customers/count it always
        // did. Only when a sales person IS picked: each route's customer
        // list is narrowed to that match, its displayed count switches from
        // the backend's r.customerCount (which doesn't know about this
        // filter) to the narrowed list's own length, and a route left with
        // zero matches is skipped entirely — otherwise it'd show an empty
        // group header with no rows under it, which reads as a bug rather
        // than "no matches here".
        const visibleRoutes = routes
            .map((r) => {
                const key = r.route || ''
                const all = customersByRoute[key] || []
                const filtered = salesPersonFilter === undefined ? all : all.filter((c) => normalizeSalesPerson(c.salesPersonNo) === salesPersonFilter)
                return { route: r, routeCustomers: filtered }
            })
            .filter(({ routeCustomers }) => salesPersonFilter === undefined || routeCustomers.length > 0)
        // Route filter <select>'s value must stay a string ('' both for "All
        // routes" — represented as undefined in state — and for "(No route)")
        // while routeFilter itself keeps the undefined/'' distinction the
        // backend needs (see fetchCustomers). ALL_ROUTES is the sentinel that
        // tells onChange which one the admin actually picked.
        const ALL_ROUTES = '__all__'
        const routeSelectValue = routeFilter === undefined ? ALL_ROUTES : routeFilter
        // Same sentinel pattern as the route filter above, for the same
        // reason: the <select>'s value must always be a plain string, while
        // salesPersonFilter itself stays undefined for "All" in state/request.
        const ALL_SALES_PERSONS = '__all_sp__'
        const salesPersonSelectValue = salesPersonFilter === undefined ? ALL_SALES_PERSONS : salesPersonFilter

        return (
            <div className="dash-page" style={{ paddingTop: '30px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '0.75rem', display: 'flex' }}>
                        <AiFillTags style={{ color: 'white', fontSize: '1.75rem' }} />
                    </div>
                    <div>
                        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Customer Price Levels</h1>
                        <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                            Assign each customer's price tier — unassigned customers use Level 1
                        </p>
                    </div>
                </div>

                <div style={{ padding: '1.5rem 2rem' }}>
                    <div className="card mb-3">
                        <div className="card-body" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search customer no. or name..." value={search}
                                        onChange={(e) => this.setState({ search: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ page: 1 }, () => this.fetchCustomers()) }} />
                                </div>
                            </div>
                            {/* Route filter (2026-08-20) — audit one route at a time */}
                            <div style={{ minWidth: '220px' }}>
                                <select className="form-select" value={routeSelectValue}
                                    onChange={(e) => {
                                        const v = e.target.value
                                        this.setState({ routeFilter: v === ALL_ROUTES ? undefined : v, page: 1 }, () => this.fetchCustomers())
                                    }}>
                                    <option value={ALL_ROUTES}>All routes</option>
                                    {routeOptions.map((r) => (
                                        <option key={r.route || '(none)'} value={r.route || ''}>
                                            {(r.route || '(No route)') + ' (' + r.customerCount + ')'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* Sales Person filter (2026-09-01) — unlike the route filter,
                                this one is NOT sent to the backend (getList() doesn't
                                support filtering by it — only returns the field). Purely
                                client-side over the customers/routes already loaded for
                                the current page — see render()'s customersByRoute usage
                                below. No fetchCustomers() call needed, so no page reset
                                either — it's instant, no network round-trip. */}
                            <div style={{ minWidth: '180px' }}>
                                <select className="form-select" value={salesPersonSelectValue}
                                    onChange={(e) => {
                                        const v = e.target.value
                                        this.setState({ salesPersonFilter: v === ALL_SALES_PERSONS ? undefined : v })
                                    }}>
                                    <option value={ALL_SALES_PERSONS}>All sales persons</option>
                                    {salesPersonOptions.map((sp) => (
                                        <option key={sp} value={sp}>{sp}</option>
                                    ))}
                                </select>
                            </div>
                            <button style={{ background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }}
                                onClick={() => this.setState({ page: 1 }, () => this.fetchCustomers())}>Search</button>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="metric-card status-danger mb-3">
                            <div style={{ color: '#DC2626', fontSize: '0.85rem' }}>{errorMessage}</div>
                        </div>
                    )}

                    <div className="card">
                        <div className="tableFixHeadSmall">
                            <table className="table table-clean">
                                <thead>
                                    <tr>
                                        <th>Customer No.</th>
                                        <th>Customer Name</th>
                                        <th>Sales Person</th>
                                        <th className="text-center">Price Level</th>
                                        <th>Last Updated By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRoutes.length === 0 && !loading && (
                                        <tr><td colSpan={5} className="empty">No results</td></tr>
                                    )}
                                    {visibleRoutes.map(({ route: r, routeCustomers }) => (
                                        <React.Fragment key={r.route || '(none)'}>
                                            {/* Route group header (2026-08-20) — spans the full table, one
                                                per route, so the client can audit a whole route at a glance.
                                                customerCount is the group's own total, unaffected by
                                                pagination since a route is never split across pages.
                                                Falls back to routeCustomers.length once the Sales Person
                                                filter narrows this group (see visibleRoutes above). */}
                                            <tr style={{ background: '#F1F5F9' }}>
                                                <td colSpan={5} style={{ fontWeight: 800, fontSize: '0.8rem', color: '#334155', padding: '8px 12px' }}>
                                                    {r.route || '(No route)'}
                                                    <span style={{ marginLeft: '8px', fontWeight: 600, fontSize: '0.72rem', color: '#64748B' }}>
                                                        {(salesPersonFilter === undefined ? r.customerCount : routeCustomers.length)} customer{(salesPersonFilter === undefined ? r.customerCount : routeCustomers.length) === 1 ? '' : 's'}
                                                    </span>
                                                </td>
                                            </tr>
                                            {routeCustomers.map((c) => {
                                                const isSaving = savingCustomerNo === c.customerNo
                                                return (
                                                    <tr key={c.customerNo}>
                                                        <td className="mono">{c.customerNo}</td>
                                                        <td className="small">{c.customerName}</td>
                                                        <td className="small">{c.salesPersonNo || '—'}</td>
                                                        <td className="text-center">
                                                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                                                                {[1, 2, 3, 4].map((lvl) => {
                                                                    const active = Number(c.priceLevel) === lvl
                                                                    const s = LEVEL_STYLES[lvl]
                                                                    return (
                                                                        <button key={lvl}
                                                                            disabled={!this.isAdmin || isSaving}
                                                                            onClick={() => this.setLevel(c.customerNo, lvl)}
                                                                            style={{
                                                                                minWidth: '34px', padding: '4px 10px', borderRadius: '999px',
                                                                                fontSize: '0.78rem', fontWeight: 700, cursor: this.isAdmin ? 'pointer' : 'default',
                                                                                background: active ? s.bg : 'transparent',
                                                                                color: active ? s.color : '#94A3B8',
                                                                                border: '1px solid ' + (active ? s.color + '55' : '#E2E8F0'),
                                                                                opacity: isSaving ? 0.6 : 1,
                                                                            }}>
                                                                            {lvl}
                                                                        </button>
                                                                    )
                                                                })}
                                                            </div>
                                                        </td>
                                                        <td className="small">{c.lastUpdatedBy || '—'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '12px 16px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Page {page} of {totalPages} ({total} routes)</span>
                            <button style={{ ...BTN_SECONDARY, opacity: page <= 1 ? 0.5 : 1 }}
                                disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCustomers())}>Previous</button>
                            <button style={{ ...BTN_SECONDARY, opacity: page >= totalPages ? 0.5 : 1 }}
                                disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCustomers())}>Next</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
